/**
 * Extracted authentication API functions
 * Shared between signup-form, login-form, and checkout flow
 */

import { object, string, boolean } from 'yup';
import { AuthResult, AuthResultSuccess, AuthResultError } from '@/app/utils/authentication';

// ============ Types ============

export interface SignupParams {
  email: string;
  password: string;
  // retypePassword: string; // confirm-password field disabled in signup form
  fullName: string;
  turnstile: string;
  receiveMarketingEmails: boolean;
}

export interface LoginParams {
  email: string;
  password: string;
  turnstile: string;
}

// Re-export auth result types for convenience
export type { AuthResult, AuthResultSuccess, AuthResultError };

// ============ Validation Schemas ============

export const signupValidationSchema = object().shape({
  name: string().min(2, 'Too short').max(100, 'Too long').required('Required'),
  email: string().email('Invalid email').required('Required'),
  password: string().min(10, 'Must be at least 10 characters').required('Required'),
  // Confirm-password field disabled in signup form:
  // passwordConfirm: string()
  //   .required('Required')
  //   .oneOf([yupRef('password')], 'Passwords do not match'),
  newsletter: boolean().default(true),
  turnstile: string().required('Please complete the CAPTCHA'),
});

export const loginValidationSchema = object().shape({
  email: string().email('Invalid email').required('Required'),
  password: string().required('Required'),
  turnstile: string().required('Please complete the CAPTCHA'),
});

// ============ API Functions ============

const API_HOST = process.env.NEXT_PUBLIC_API_HOST || '';

/**
 * Perform user signup via API
 */
export async function performSignup(params: SignupParams): Promise<AuthResult> {
  const url = API_HOST + '/api/sign_ins/signup';

  const data = {
    email: params.email,
    password: params.password,
    // retype_password disabled in signup form; not sent to API
    full_name: params.fullName,
    client: 'penzu_web',
    turnstile: params.turnstile,
    receive_marketing_emails: params.receiveMarketingEmails,
  };

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    body: JSON.stringify(data),
  });

  try {
    const result = await response.json();
    if ((result as AuthResultError).error || (result as AuthResultSuccess).access_token) {
      return result;
    }
    throw new Error('Bad response from server: ' + JSON.stringify(result));
  } catch (error) {
    console.error('Unexpected error signing up: ', error);
    let message = 'Sorry there was a problem signing up. Please try again.';
    if (error instanceof Error) {
      message = error.message;
    }
    return {
      error: {
        title: 'Signup failed',
        message,
      },
    };
  }
}

/**
 * Perform user login via API
 */
export async function performLogin(params: LoginParams): Promise<AuthResult> {
  const url = API_HOST + '/api/sign_ins/login';

  const data = {
    client: 'penzu_web',
    email: params.email,
    password: params.password,
    turnstile: params.turnstile,
  };

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    body: JSON.stringify(data),
  });

  try {
    const result = await response.json();
    if ((result as AuthResultError).error || (result as AuthResultSuccess).access_token) {
      return result;
    }
    throw new Error('Bad response from server: ' + JSON.stringify(result));
  } catch (error) {
    console.error('Unexpected error logging in: ', error);
    let message = 'Sorry there was a problem logging in. Please try again.';
    if (error instanceof Error) {
      message = error.message;
    }
    return {
      error: {
        title: 'Login failed',
        message,
      },
    };
  }
}

// ============ Type Guards ============

/**
 * Check if an auth result is a success response
 */
export function isAuthSuccess(result: AuthResult): result is AuthResultSuccess {
  return 'access_token' in result;
}

/**
 * Check if an auth result is an error response
 */
export function isAuthError(result: AuthResult): result is AuthResultError {
  return 'error' in result;
}
